import type { JSX } from "react";
import { Fragment } from "react";

import type { SectionRenderInput } from "./types";

/** `112` -> `tel:112`. Anything that is not a dialable number stays plain text. */
function telHref(value: string): string | null {
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length >= 3 ? `tel:${digits}` : null;
}

/**
 * ESSENTIALS — the emergency numbers as dialable cells, then the authored facts
 * as a definition list. Both halves are conditional: a guide with numbers but no
 * prose, or the other way round, prints the half it has.
 */
export function EssentialsSection({ page }: SectionRenderInput): JSX.Element {
  const essentials = page.essentialInfo;
  const facts: { label: string; value: string }[] = [];
  if (essentials.banks) facts.push({ label: "BANKS", value: essentials.banks });
  if (essentials.gettingAround) {
    facts.push({ label: "GETTING AROUND", value: essentials.gettingAround });
  }
  if (essentials.foodDrink) facts.push({ label: "FOOD & DRINK", value: essentials.foodDrink });
  if (essentials.mustTry) facts.push({ label: "MUST TRY", value: essentials.mustTry });

  return (
    <>
      {essentials.emergency && (
        <ul className="atlas-guide-sos">
          {(
            [
              ["POLICE", essentials.emergency.police],
              ["FIRE", essentials.emergency.fire],
              ["MEDICAL", essentials.emergency.medical],
            ] as const
          ).map(([label, value]) => {
            const href = telHref(value);
            return (
              <li key={label} className="atlas-guide-tile atlas-guide-sos-cell">
                <p className="atlas-guide-sos-label">{label}</p>
                {href ? (
                  <a className="atlas-guide-sos-value" href={href}>
                    {value}
                  </a>
                ) : (
                  <p className="atlas-guide-sos-value">{value}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {facts.length > 0 && (
        <dl className="atlas-guide-facts">
          {facts.map((fact) => (
            <Fragment key={fact.label}>
              <dt className="atlas-guide-fact-label">{fact.label}</dt>
              <dd className="atlas-guide-fact-value">{fact.value}</dd>
            </Fragment>
          ))}
        </dl>
      )}
    </>
  );
}
